import { useEffect, useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { errorMessage, validatePickupCode, verifyDeliveryCode } from "./api";

const MODES = {
  // Entrega própria: o CLIENTE recebeu o código no app; o entregador pergunta e digita.
  delivery: {
    title: "Código de entrega do iFood",
    hint: (codigo) => <>Pedido <b>{codigo}</b>: peça ao cliente o código de 4 números que o iFood mandou para ele e digite aqui.</>,
    button: "Confirmar entrega",
    ok: "Código de entrega confirmado no iFood",
    call: verifyDeliveryCode,
  },
  // Entrega iFood: o ENTREGADOR informa o código na coleta; a loja digita para liberar.
  pickup: {
    title: "Código de coleta do entregador",
    hint: (codigo) => <>Pedido <b>{codigo}</b>: o entregador do iFood informa um código de 4 números na coleta. Digite aqui para liberar o pedido.</>,
    button: "Confirmar coleta",
    ok: "Código de coleta validado no iFood",
    call: validatePickupCode,
  },
};

/** Validação de código do iFood. `mode="delivery"` (padrão): entrega própria, código
 *  do cliente, usado no Delivery e na tela do entregador. `mode="pickup"`: entrega
 *  iFood, código do entregador na coleta, usado no Delivery (coluna PRONTO). */
export default function IfoodDeliveryCodeDialog({ pedido, open, onClose, onVerified, onSkip, mode = "delivery" }) {
  const cfg = MODES[mode] || MODES.delivery;
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [skipArmed, setSkipArmed] = useState(false);

  useEffect(() => { if (open) { setCode(""); setSkipArmed(false); } }, [open]);

  const verify = async (e) => {
    e?.preventDefault?.();
    if (code.replace(/\D/g, "").length < 4) { toast.error("O código tem 4 números"); return; }
    setBusy(true);
    try {
      await cfg.call(pedido.id, code.replace(/\D/g, ""));
      toast.success(cfg.ok);
      onVerified?.();
    } catch (err) {
      toast.error(errorMessage(err, "Código não confirmado"), { duration: 7000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!open} onOpenChange={(o) => !o && !busy && onClose?.()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-red-500" /> {cfg.title}</DialogTitle>
          <DialogDescription>{cfg.hint(pedido?.codigo)}</DialogDescription>
        </DialogHeader>
        <form onSubmit={verify} className="space-y-3">
          <Input
            id="ifood-delivery-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="0000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="text-center text-3xl font-mono tracking-[0.4em] h-14"
            autoFocus
          />
          <Button type="submit" disabled={busy || code.length < 4} className="w-full h-11 bg-red-600 hover:bg-red-700 text-white font-bold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />} {cfg.button}
          </Button>
        </form>
        {onSkip && (
          <DialogFooter className="sm:justify-start">
            {skipArmed ? (
              <div className="text-xs space-y-2 w-full">
                <p className="text-amber-700 dark:text-amber-400">Sem o código o iFood pode contestar a entrega. Marque assim só se o cliente não tem o código.</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setSkipArmed(false)}>Voltar</Button>
                  <Button variant="outline" size="sm" className="text-amber-700" onClick={onSkip}>Entregar sem código mesmo assim</Button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => setSkipArmed(true)} className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2">
                Cliente não tem o código
              </button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
